import { Routes } from '@angular/router';
import { accountHistoryResolver } from '@df/account/resolvers/account-history.resolver';
import { countriesListResolver } from '@df/api/config/contries-list.resolver';
import { CustomerHistoryOrderResolver } from '@df/session/customer/customer-history-order.resolver';
import { CustomerLoggedGuard } from '@df/session/customer/customer-logged.guard';
import { wishlistLoadResolver } from '@df/session/wishlist/wishlist-load.resolver';
import { IsTradeStoreGuard } from '../multisite/trade/is-trade-store.guard';
import { accountAntavoRefreshResolver } from './account-antavo-refresh.resolver';
import { BulkOrderResolver } from './bulk-order/bulk-order.resolver';
import { LocalCustomerHistoryResolver } from './customer-history.resolver';
import { isInAppCustomerGuard } from './guards/is-in-app-customer.guard';
import { accountMissionsResolver } from './pwa/missions/account-missions.resolver';
import { accountMissionDetailsResolver } from './pwa/missions/details/account-mission-details.resolver';
import { accountMyOutfitsResolver } from './pwa/my-outfits/account-my-outfits.resolver';
import { accountProfilesResolver } from './pwa/profiles/account-profiles.resolver';
import { horseEditResolver } from './pwa/profiles/horse-edit/horse-edit.resolver';
import { memberEditResolver } from './pwa/profiles/member-edit/member-edit.resolver';
import { accountRewardsResolver } from './pwa/rewards/account-rewards.resolver';
import { AccountViewCmsResolver } from './view/account-view-cms.resolver';
import { IAccountRouteData } from './view/account-view.component';

import { accountDeleteResolver } from './pwa/delete/account-delete.resolver';

declare module '@df/account/account.interfaces' {
    namespace Account {
        interface INavigationItem {
            icon?: string;
            trade?: boolean;
            stores?: string[];
            standaloneOnly?: boolean;
            twoColumns?: boolean;
        }
        interface IEditFormData {
            prefix?: string;
            dob?: string;
        }
    }
}
declare module '@df/session/customer/customer.interfaces' {
    namespace Customer {
        interface ICustomerData {
            prefix?: string;
            dob?: string;
            credit_available?: string;
        }
    }
}

export const accountRoutes: Routes = [
    {
        path: '',
        loadComponent: () =>
            import(
                /* webpackChunkName: "account-view-cms.async" */
                './view/account-view-cms.component'
            ).then(m => m.AccountViewCmsComponent),
        canActivate: [CustomerLoggedGuard],
        resolve: {
            layout: AccountViewCmsResolver,
            profilesData: accountProfilesResolver,
            antavoRefresh: accountAntavoRefreshResolver
        },
        children: [
            {
                path: '',
                pathMatch: 'full',
                loadComponent: () =>
                    import(
                        /* webpackChunkName: "account-dashboard.async" */
                        './dashboard/account-dashboard.component'
                    ).then(m => m.AccountDashboardComponent),
                resolve: {
                    countries: countriesListResolver,
                    history: accountHistoryResolver,
                    antavoRefresh: accountAntavoRefreshResolver
                }
            },
            {
                path: 'edit',
                resolve: {
                    profilesData: accountProfilesResolver
                },
                loadComponent: () =>
                    import(
                        /* webpackChunkName: "account-edit.async" */
                        './edit/account-edit.component'
                    ).then(m => m.AccountEditComponent)
            },
            {
                path: 'edit/email',
                loadComponent: () =>
                    import(
                        /* webpackChunkName: "account-edit-email.async" */
                        './edit/email/account-edit-email.component'
                    ).then(m => m.AccountEditEmailComponent),
                data: {
                    contentCols: 5
                } as IAccountRouteData
            },
            {
                path: 'edit/password',
                loadComponent: () =>
                    import(
                        /* webpackChunkName: "account-edit-password.async" */
                        './edit/password/account-edit-password.component'
                    ).then(m => m.AccountEditPasswordComponent),
                data: {
                    contentCols: 5
                } as IAccountRouteData
            },
            {
                path: 'edit/personal',
                loadComponent: () =>
                    import(
                        /* webpackChunkName: "account-edit-personal.async" */
                        './edit/personal/account-edit-personal.component'
                    ).then(m => m.AccountEditPersonalComponent),
                data: {
                    contentCols: 5
                } as IAccountRouteData
            },
            {
                path: 'payment-methods',
                loadComponent: () =>
                    import(
                        /* webpackChunkName: "account-saved-card-book.async" */
                        './saved-card/book/account-saved-card-book.component'
                    ).then(m => m.AccountSavedCardBookComponent)
            },
            {
                path: 'address-book',
                loadComponent: () =>
                    import(
                        /* webpackChunkName: "account-address-book.async" */
                        './address/book/account-address-book.component'
                    ).then(m => m.AccountAddressBookComponent),
                data: {
                    contentCols: 8
                } as IAccountRouteData
            },
            {
                path: 'sales-contact',
                loadComponent: () =>
                    import(
                        /* webpackChunkName: "account-sales-contact.async" */
                        './sales-contact/account-sales-contact.component'
                    ).then(m => m.AccountSalesContactComponent),
                canActivate: [IsTradeStoreGuard]
            },
            {
                path: 'allowed-categories',
                loadComponent: () =>
                    import(
                        /* webpackChunkName: "account-allowed-categories.async" */
                        './allowed-categories/account-allowed-categories.component'
                    ).then(m => m.AccountAllowedCategoriesComponent)
            },
            {
                path: 'information-centre',
                loadComponent: () =>
                    import(
                        /* webpackChunkName: "account-information-centre.async" */
                        './information-centre/account-information-centre.component'
                    ).then(m => m.AccountInformationCentreComponent)
            },
            {
                path: 'address',
                loadComponent: () =>
                    import(
                        /* webpackChunkName: "account-address-edit.async" */
                        './address/edit/account-address-edit.component'
                    ).then(m => m.AccountAddressEditComponent),
                resolve: {
                    appMore: countriesListResolver
                },
                data: {
                    contentCols: 5
                } as IAccountRouteData
            },
            {
                path: 'address/:id',
                loadComponent: () =>
                    import(
                        /* webpackChunkName: "account-address-edit.async" */
                        './address/edit/account-address-edit.component'
                    ).then(m => m.AccountAddressEditComponent),
                resolve: {
                    appMore: countriesListResolver
                },
                data: {
                    contentCols: 5
                } as IAccountRouteData
            },
            {
                path: 'my-orders',
                loadComponent: () =>
                    import(
                        /* webpackChunkName: "account-order-history.async" */
                        './order/history/account-order-history.component'
                    ).then(m => m.AccountOrderHistoryComponent),
                resolve: {
                    appMore: countriesListResolver,
                    history: LocalCustomerHistoryResolver
                },
                data: {
                    contentCols: 9
                } as IAccountRouteData
            },
            {
                path: 'my-orders/:id',
                loadComponent: () =>
                    import(
                        /* webpackChunkName: "account-order-view.async" */
                        './order/view/account-order-view.component'
                    ).then(m => m.AccountOrderViewComponent),
                resolve: {
                    appMore: countriesListResolver,
                    historyOrder: CustomerHistoryOrderResolver
                },
                data: {
                    contentCols: 6
                } as IAccountRouteData
            },
            {
                path: 'wishlist',
                loadComponent: () =>
                    import(
                        /* webpackChunkName: "account-wishlist.async" */
                        './wishlist/account-wishlist.component'
                    ).then(m => m.AccountWishlistComponent),
                resolve: {
                    wishlist: wishlistLoadResolver
                },
                data: {
                    contentCols: 9
                } as IAccountRouteData
            },
            {
                path: 'quick-buy',
                loadComponent: () =>
                    import(
                        /* webpackChunkName: "bulk-order.async" */
                        './bulk-order/bulk-order.component'
                    ).then(m => m.BulkOrderComponent),
                canActivate: [IsTradeStoreGuard],
                resolve: {
                    assets: BulkOrderResolver
                },
                data: {
                    fullPage: true
                } as IAccountRouteData
            },
            {
                path: 'refer-a-friend',
                loadComponent: () =>
                    import(
                        /* webpackChunkName: "account-mention-me-wrapper.async" */
                        './mention-me/account-mention-me-wrapper.component'
                    ).then(m => m.AccountMentionMeWrapperComponent)
            },
            {
                path: 'stock-level-report',
                loadComponent: () =>
                    import(
                        /* webpackChunkName: "account-stock-level-report.async" */
                        './stock-level-report/account-stock-level-report.component'
                    ).then(m => m.AccountStockLevelReportComponent),
                canActivate: [IsTradeStoreGuard]
            },
            {
                path: 'earn-points',
                loadComponent: () =>
                    import(
                        /* webpackChunkName: "account-earn-points.async" */
                        './pwa/earn-points/account-earn-points.component'
                    ).then(m => m.AccountEarnPointsComponent),
                canActivate: [isInAppCustomerGuard],
                data: {
                    contentCols: 8
                } as IAccountRouteData
            },
            {
                path: 'my-missions',
                loadComponent: () =>
                    import(
                        /* webpackChunkName: "account-missions.async" */
                        './pwa/missions/account-missions.component'
                    ).then(m => m.AccountMissionsComponent),
                canActivate: [isInAppCustomerGuard],
                resolve: {
                    missions: accountMissionsResolver,
                    antavoRefresh: accountAntavoRefreshResolver
                },
                data: {
                    contentCols: 8
                } as IAccountRouteData
            },
            {
                path: 'my-missions/:id',
                loadComponent: () =>
                    import(
                        /* webpackChunkName: "account-mission-details.async" */
                        './pwa/missions/details/account-mission-details.component'
                    ).then(m => m.AccountMissionDetailsComponent),
                canActivate: [isInAppCustomerGuard],
                resolve: {
                    mission: accountMissionDetailsResolver,
                    antavoRefresh: accountAntavoRefreshResolver
                },
                data: {
                    contentCols: 8
                } as IAccountRouteData
            },
            {
                path: 'my-rewards',
                loadComponent: () =>
                    import(
                        /* webpackChunkName: "account-rewards.async" */
                        './pwa/rewards/account-rewards.component'
                    ).then(m => m.AccountRewardsComponent),
                canActivate: [isInAppCustomerGuard],
                resolve: {
                    rewards: accountRewardsResolver,
                    antavoRefresh: accountAntavoRefreshResolver
                },
                data: {
                    contentCols: 8
                } as IAccountRouteData
            },
            {
                path: 'my-profiles',
                loadComponent: () =>
                    import(
                        /* webpackChunkName: "account-profiles.async" */
                        './pwa/profiles/account-profiles.component'
                    ).then(m => m.AccountProfilesComponent),
                canActivate: [isInAppCustomerGuard],
                resolve: {
                    antavoRefresh: accountAntavoRefreshResolver
                },
                data: {
                    contentCols: 8
                } as IAccountRouteData
            },
            {
                path: 'my-outfits',
                loadComponent: () =>
                    import(
                        /* webpackChunkName: "account-my-outfits.async" */
                        './pwa/my-outfits/account-my-outfits.component'
                    ).then(m => m.AccountMyOutfitsComponent),
                canActivate: [isInAppCustomerGuard],
                resolve: {
                    cmsBlock: accountMyOutfitsResolver,
                    antavoRefresh: accountAntavoRefreshResolver
                },
                data: {
                    contentCols: 8
                } as IAccountRouteData
            },
            {
                path: 'delete',
                loadComponent: () =>
                    import(
                        /* webpackChunkName: "account-delete.async" */
                        './pwa/delete/account-delete.component'
                    ).then(m => m.AccountDeleteComponent),
                canActivate: [isInAppCustomerGuard],
                resolve: {
                    cmsBlock: accountDeleteResolver
                },
                data: {
                    contentCols: 8
                } as IAccountRouteData
            },

            {
                path: 'settings',
                loadComponent: () =>
                    import(
                        /* webpackChunkName: "account-settings.async" */
                        './settings/account-settings.component'
                    ).then(m => m.AccountSettingsComponent),
                canActivate: [isInAppCustomerGuard],
                data: {
                    contentCols: 8
                } as IAccountRouteData
            },
            {
                path: 'profile/customer/edit',
                loadComponent: () =>
                    import(
                        /* webpackChunkName: "member-edit.async" */
                        './pwa/profiles/member-edit/member-edit.component'
                    ).then(m => m.MemberEditComponent),
                canActivate: [isInAppCustomerGuard],
                resolve: {
                    cmsBlock: memberEditResolver,
                    appMore: countriesListResolver,
                    antavoRefresh: accountAntavoRefreshResolver
                },
                data: {
                    contentCols: 8
                } as IAccountRouteData
            },
            {
                path: 'profile/member/edit/:index',
                loadComponent: () =>
                    import(
                        /* webpackChunkName: "member-edit.async" */
                        './pwa/profiles/member-edit/member-edit.component'
                    ).then(m => m.MemberEditComponent),
                canActivate: [isInAppCustomerGuard],
                resolve: {
                    cmsBlock: memberEditResolver,
                    appMore: countriesListResolver,
                    antavoRefresh: accountAntavoRefreshResolver
                },
                data: {
                    contentCols: 8,
                    isFamilyMember: true
                } as IAccountRouteData
            },
            {
                path: 'profile/horse/edit/:index',
                loadComponent: () =>
                    import(
                        /* webpackChunkName: "horse-edit.async" */
                        './pwa/profiles/horse-edit/horse-edit.component'
                    ).then(m => m.HorseEditComponent),
                canActivate: [isInAppCustomerGuard],
                resolve: {
                    cmsBlock: horseEditResolver,
                    antavoRefresh: accountAntavoRefreshResolver
                },
                data: {
                    contentCols: 8
                } as IAccountRouteData
            }
        ]
    }
];
